<template>
    <div v-if="isPageLoaded" class="housing-landing">
        <!-- Section 1: Hero Banner -->
        <div class="hero-section">
            <div class="hero-content-wrapper">
                <v-row align="center" class="ma-0">
                    <v-col cols="12" md="6" class="hero-text-col">
                        <h1 class="hero-title">{{ heroTitle }}</h1>
                        <p class="hero-description" v-html="page.housingDescription"></p>
                        <p class="hero-cta-text mb-3">Start Your Search Now</p>
                        <v-btn
                            color="primary"
                            depressed
                            large
                            :to="{ name: 'SchoolQuestionnaire', params: { schoolSlug } }"
                            class="hero-btn"
                        >
                            Sign Up Today!
                        </v-btn>
                    </v-col>
                    <v-col cols="12" md="6" class="hero-image-col">
                        <div class="hero-image-wrapper">
                            <v-img
                                v-if="heroImageUrl"
                                :src="heroImageUrl"
                                class="hero-image"
                                aspect-ratio="1.5"
                            />
                            <div v-if="schoolLogoUrl" class="school-logo-overlay">
                                <v-img
                                    :src="schoolLogoUrl"
                                    max-width="180"
                                    contain
                                />
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Donation Ticker -->
        <DonationTicker v-if="page.schoolId" :schoolId="page.schoolId" />

        <!-- Section 1.5: Start Here - Housing Comparison -->
        <div class="start-here-section section">
            <div class="content">
                <v-row justify="center" class="mb-6">
                    <v-col cols="12" class="text-center">
                        <h2 class="section-title">Choose Your Housing Type</h2>
                        <p class="section-description">EDUrent offers two housing options to fit your needs and budget.</p>
                    </v-col>
                </v-row>

                <v-row justify="center">
                    <v-col cols="12" sm="6" md="5" class="housing-card-col">
                        <div class="housing-card">
                            <div class="housing-card-header shared">
                                <v-icon size="48" color="white">mdi-account-group</v-icon>
                                <h3>Shared Housing</h3>
                            </div>
                            <div class="housing-card-body">
                                <ul class="housing-features">
                                    <li>
                                        <v-icon color="success" size="20">mdi-check-circle</v-icon>
                                        <span>Lower monthly rent</span>
                                    </li>
                                    <li>
                                        <v-icon color="success" size="20">mdi-check-circle</v-icon>
                                        <span>Furnished rooms available</span>
                                    </li>
                                    <li>
                                        <v-icon color="success" size="20">mdi-check-circle</v-icon>
                                        <span>Utilities often included</span>
                                    </li>
                                    <li>
                                        <v-icon color="success" size="20">mdi-check-circle</v-icon>
                                        <span>Flexible lease terms</span>
                                    </li>
                                    <li>
                                        <v-icon color="success" size="20">mdi-check-circle</v-icon>
                                        <span>Great for meeting classmates</span>
                                    </li>
                                </ul>
                                <p class="housing-ideal">Ideal for students seeking affordability and community.</p>
                            </div>
                        </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="5" class="housing-card-col">
                        <div class="housing-card">
                            <div class="housing-card-header direct">
                                <v-icon size="48" color="white">mdi-home-account</v-icon>
                                <h3>Direct Lease</h3>
                            </div>
                            <div class="housing-card-body">
                                <ul class="housing-features">
                                    <li>
                                        <v-icon color="success" size="20">mdi-check-circle</v-icon>
                                        <span>Private apartment or studio</span>
                                    </li>
                                    <li>
                                        <v-icon color="warning" size="20">mdi-alert-circle</v-icon>
                                        <span>Credit check required</span>
                                    </li>
                                    <li>
                                        <v-icon color="warning" size="20">mdi-alert-circle</v-icon>
                                        <span>Proof of income needed</span>
                                    </li>
                                    <li>
                                        <v-icon color="warning" size="20">mdi-alert-circle</v-icon>
                                        <span>Must qualify for lease</span>
                                    </li>
                                    <li>
                                        <v-icon color="success" size="20">mdi-check-circle</v-icon>
                                        <span>Build rental history</span>
                                    </li>
                                </ul>
                                <p class="housing-ideal">For students who qualify and want full independence.</p>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <v-row justify="center" class="mt-8">
                    <v-col cols="12" class="text-center">
                        <v-btn
                            color="primary"
                            depressed
                            x-large
                            :to="{ name: 'SchoolQuestionnaire', params: { schoolSlug } }"
                            class="start-here-btn"
                        >
                            Get Started
                            <v-icon right>mdi-arrow-right</v-icon>
                        </v-btn>
                        <p class="start-here-subtext">Answer a few questions to find your perfect match</p>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Section 2: Stats -->
        <div v-if="hasStats" class="stats-section section">
            <div class="content">
                <v-row justify="center">
                    <v-col cols="12" sm="4" class="stat-item">
                        <div class="stat-number">{{ formatStatNumber(page.propertyPartners) }}</div>
                        <div class="stat-label">Property Partners</div>
                    </v-col>
                    <v-col cols="12" sm="4" class="stat-item">
                        <div class="stat-number">{{ formatStatNumber(page.unitsSecured) }}</div>
                        <div class="stat-label">Units Secured</div>
                    </v-col>
                    <v-col cols="12" sm="4" class="stat-item">
                        <div class="stat-number">{{ formatStatNumber(page.headsInBeds) }}</div>
                        <div class="stat-label">Heads in Beds</div>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Section 3: Features & Benefits -->
        <div v-if="hasFeatures" class="features-section section section-light">
            <div class="content">
                <v-row justify="center" class="mb-6">
                    <v-col cols="12" class="text-center">
                        <v-img
                            v-if="featuresLogoUrl"
                            :src="featuresLogoUrl"
                            max-width="200"
                            class="mx-auto mb-4"
                            contain
                        />
                        <h2 class="section-title">{{ page.featuresTitle || 'Shared Housing Program Features & Benefits' }}</h2>
                        <p v-if="page.featuresDescription" v-html="page.featuresDescription" class="section-description mt-4"></p>
                    </v-col>
                </v-row>

                <v-row justify="center">
                    <v-col
                        v-for="feature in activeFeatures"
                        :key="feature.id"
                        cols="12"
                        sm="6"
                        md="4"
                        class="feature-col"
                    >
                        <div class="feature-card">
                            <v-icon color="#afdf62" size="28" class="feature-icon">
                                mdi-check-circle-outline
                            </v-icon>
                            <div class="feature-content">
                                <h4 class="feature-title">{{ feature.featureTitle }}</h4>
                                <p v-if="feature.featureDescription" class="feature-description">
                                    {{ feature.featureDescription }}
                                </p>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Section 3.5: Get Started (Simple Steps) -->
        <div v-if="hasCallToActions" class="simple-steps-section section">
            <div class="content">
                <v-row justify="center" class="mb-4">
                    <v-col cols="12" class="text-center">
                        <h2 class="section-title">{{ callToActionsTitle }}</h2>
                    </v-col>
                </v-row>

                <v-row justify="center">
                    <v-col cols="12">
                        <SimpleSteps
                            :callToActions="callToActionsSteps"
                        />
                    </v-col>
                </v-row>

                <v-row justify="center" class="mt-8">
                    <v-col cols="12" class="text-center">
                        <v-btn
                            color="primary"
                            depressed
                            large
                            :to="{ name: 'SchoolQuestionnaire', params: { schoolSlug } }"
                            class="cta-steps-btn"
                        >
                            Create Account
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Section 4: How It Works -->
        <div v-if="hasHowItWorks" class="how-it-works-section section">
            <div class="content">
                <v-row justify="center" class="mb-8">
                    <v-col cols="12" class="text-center">
                        <h2 class="section-title">{{ page.howItWorksTitle || 'How It Works' }}</h2>
                    </v-col>
                </v-row>

                <v-row align="center">
                    <v-col cols="12" md="6" class="how-it-works-image-col">
                        <v-img
                            v-if="howItWorksImageUrl"
                            :src="howItWorksImageUrl"
                            class="how-it-works-image rounded-lg"
                        />
                    </v-col>
                    <v-col cols="12" md="6" class="how-it-works-steps-col">
                        <div
                            v-for="step in howItWorksSteps"
                            :key="step.id || step.stepNumber"
                            class="step-item"
                        >
                            <div class="step-number-wrapper">
                                <span class="step-number">{{ step.stepNumber }}</span>
                            </div>
                            <div class="step-content">
                                <h4 class="step-title">{{ step.stepTitle }}</h4>
                                <p class="step-description">{{ step.stepDescription }}</p>
                            </div>
                        </div>

                        <v-btn
                            color="primary"
                            depressed
                            :to="{ name: 'SchoolQuestionnaire', params: { schoolSlug } }"
                            class="mt-8 how-it-works-btn"
                        >
                            {{ page.howItWorksCta || 'CREATE ACCOUNT!' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Section 5: Testimonials -->
        <div v-if="hasTestimonials" class="testimonials-section section section-light">
            <div class="content">
                <v-row justify="center">
                    <v-col
                        v-for="testimonial in testimonials"
                        :key="testimonial.id"
                        cols="12"
                        md="4"
                        class="testimonial-col"
                    >
                        <div class="testimonial-card">
                            <div class="quote-icon-wrapper">
                                <div class="quote-icon-circle">
                                    <v-icon size="24" color="#FFFFFF">
                                        mdi-format-quote-close
                                    </v-icon>
                                </div>
                            </div>
                            <div class="stars-wrapper">
                                <v-icon
                                    v-for="star in 5"
                                    :key="star"
                                    :color="star <= (testimonial.rating || 5) ? '#F9A825' : '#E0E0E0'"
                                    size="22"
                                >
                                    mdi-star
                                </v-icon>
                            </div>
                            <p class="testimonial-quote">{{ testimonial.quote }}</p>
                            <p class="testimonial-name">{{ testimonial.studentName }}</p>
                        </div>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Section 6: CTA Footer -->
        <div class="cta-footer-section section section-dark">
            <div class="content text-center">
                <v-row justify="center">
                    <v-col cols="12" md="8">
                        <h2 class="cta-title">{{ page.ctaTitle || 'Sign Up for Free & Start Your Housing Search Today!' }}</h2>
                        <p v-if="ctaDescriptionText" v-html="ctaDescriptionText" class="cta-description mt-4 mb-6"></p>
                        <p class="cta-subtitle">Find Your Perfect Home</p>
                        <v-btn
                            color="primary"
                            depressed
                            x-large
                            :to="{ name: 'SchoolQuestionnaire', params: { schoolSlug } }"
                            class="cta-btn mt-4"
                        >
                            {{ page.ctaButtonText || 'Register Now!' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script>
import SiteService from '@/services/site.service';
import { getMediaPathUrl, mapCollectionFromCMS } from '@/utils/helpers';
import DonationTicker from '@/components/DonationTicker';
import SimpleSteps from '@/components/SimpleSteps';

export default {
    name: 'HousingLanding',

    components: {
        DonationTicker,
        SimpleSteps,
    },

    props: {
        schoolSlug: {
            type: String,
            default: ''
        }
    },

    metaInfo() {
        return {
            title: this.metaTitle,
            meta: [
                { name: 'description', content: this.metaDescription },
                { name: 'keywords', content: this.keywords }
            ]
        };
    },

    data() {
        return {
            metaTitle: 'Housing',
            metaDescription: 'EDUrent\'s shared housing program provides the traditional college experience. Students share a space with classmates and make new friends on day one at move-in.',
            keywords: '',
            pageName: 'Housing Landing',
            page: null,
        };
    },

    computed: {
        isPageLoaded() {
            return this.page?.pageSlug?.length > 0;
        },

        schoolName() {
            return this.page?.schoolName || 'Your School';
        },

        heroTitle() {
            const title = this.page?.housingTitle || 'Find Your Ideal Student Apartment Near [School Name]';
            return title.replace(/\[School Name\]/gi, this.schoolName);
        },

        heroImageUrl() {
            return this.getImageUrl(this.page?.backgroundImage);
        },

        schoolLogoUrl() {
            return this.getImageUrl(this.page?.schoolLogo);
        },

        featuresLogoUrl() {
            return this.getImageUrl(this.page?.featuresLogo);
        },

        howItWorksImageUrl() {
            return this.getImageUrl(this.page?.howItWorksImage);
        },

        activeFeatures() {
            try {
                const features = mapCollectionFromCMS(this.page?.features);
                return features.filter(f => f.featureEnabled !== false);
            } catch {
                return [];
            }
        },

        howItWorksSteps() {
            try {
                return mapCollectionFromCMS(this.page?.howItWorksSteps);
            } catch {
                return [];
            }
        },

        testimonials() {
            try {
                return mapCollectionFromCMS(this.page?.testimonials);
            } catch {
                return [];
            }
        },

        hasStats() {
            return this.page?.propertyPartners || this.page?.unitsSecured || this.page?.headsInBeds;
        },

        hasFeatures() {
            return this.activeFeatures.length > 0;
        },

        hasHowItWorks() {
            return this.howItWorksSteps.length > 0;
        },

        hasTestimonials() {
            return this.testimonials.length > 0;
        },

        ctaDescriptionText() {
            const desc = this.page?.ctaDescription || '';
            return desc.replace(/\[School Name\]/gi, this.schoolName);
        },

        callToActionsSteps() {
            try {
                const steps = mapCollectionFromCMS(this.page?.call_to_actions);
                return steps.map(step => ({
                    id: step.id,
                    title: step.title,
                    description: step.description,
                    icon: {
                        url: step.icon?.data?.attributes?.url || ''
                    }
                }));
            } catch {
                return [];
            }
        },

        hasCallToActions() {
            return this.callToActionsSteps.length > 0;
        },

        callToActionsTitle() {
            return this.page?.callToActionMainTitle || 'Get Started With EDUrent in 3 Simple Steps';
        }
    },

    watch: {
        schoolSlug: {
            handler: function () {
                this.init();
            },
            immediate: true
        }
    },

    methods: {
        async init() {
            this.$store.commit('setHeaderMenuState', '');
            const response = await SiteService.getHousingLandingPage(this.schoolSlug);

            if (response == null) {
                return this.$router.push('/');
            }

            const page = response?.[0]?.attributes;
            const schoolSlug = page?.pageSlug;

            if (!page || !schoolSlug) {
                return this.$router.push('/');
            }

            this.page = page;

            // Update meta info from CMS if available
            if (page.metaTitle) {
                this.metaTitle = page.metaTitle;
            }
            if (page.metaDescription) {
                this.metaDescription = page.metaDescription;
            }
            if (page.keywords) {
                this.keywords = page.keywords;
            }
        },

        getImageUrl(media) {
            if (!media) return '';
            try {
                return getMediaPathUrl(media);
            } catch (e) {
                return '';
            }
        },

        formatStatNumber(num) {
            if (!num && num !== 0) return '0';
            return num.toLocaleString();
        }
    },
};
</script>

<style lang="scss" scoped>
.housing-landing {
    display: flex;
    flex-direction: column;
    font-family: 'Poppins', sans-serif;
}

// Section base styles
.section {
    padding: 60px 0;

    .content {
        width: 90%;
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
    }
}

.section-light {
    background-color: #F5F5F5;
}

.section-dark {
    background-color: #1A1A2E;

    h2, p {
        color: #FFFFFF;
    }
}

.section-title {
    font-size: 36px;
    font-weight: 700;
    color: #1A1A2E;
    margin-bottom: 16px;
}

.section-description {
    font-size: 16px;
    color: #5D6E7F;
    max-width: 700px;
    margin: 0 auto;
}

// Hero Section
.hero-section {
    background: linear-gradient(180deg, #F0F3FE 0%, #F0F3FE 100%);
    padding: 80px 0 60px;
    margin-top: -66px;
    padding-top: 120px;
    min-height: 500px;
    display: flex;
    align-items: center;
}

.hero-content-wrapper {
    width: 90%;
    max-width: 1300px;
    margin: 0 auto;
    padding: 0 40px;

    @media (max-width: 959px) {
        padding: 0 20px;
    }
}

.hero-text-col {
    padding-right: 60px;

    @media (max-width: 959px) {
        padding-right: 12px;
        text-align: center;
        margin-bottom: 40px;
    }
}

.hero-title {
    font-size: 52px;
    font-weight: 700;
    line-height: 1.15;
    color: #1A1A2E;
    margin-bottom: 24px;

    @media (max-width: 1264px) {
        font-size: 42px;
    }

    @media (max-width: 959px) {
        font-size: 36px;
    }

    @media (max-width: 599px) {
        font-size: 28px;
    }
}

.hero-description {
    font-size: 16px;
    color: #4A5568;
    line-height: 1.7;
    margin-bottom: 24px;

    ::v-deep p {
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }
    }
}

.hero-cta-text {
    font-size: 16px;
    font-weight: 700;
    color: #1A1A2E;
    margin-bottom: 16px;
}

.hero-btn {
    min-width: 180px;
    height: 48px !important;
    font-size: 15px !important;
    font-weight: 600;
    text-transform: none;
    border-radius: 6px;
    letter-spacing: 0;
}

.hero-image-col {
    display: flex;
    justify-content: flex-end;

    @media (max-width: 959px) {
        justify-content: center;
    }
}

.hero-image-wrapper {
    position: relative;
    max-width: 520px;
    width: 100%;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.hero-image {
    border-radius: 20px;
}

.school-logo-overlay {
    position: absolute;
    bottom: 20px;
    right: 20px;
    background: rgba(255, 255, 255, 0.95);
    padding: 14px 20px;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

// Stats Section
.stats-section {
    background: #FFFFFF;
    border-bottom: 1px solid #E0E0E0;
}

.stat-item {
    text-align: center;
    padding: 20px;
    position: relative;

    // Add vertical divider between stats (not on last item)
    &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        height: 60%;
        width: 1px;
        background-color: #E0E0E0;

        @media (max-width: 599px) {
            display: none;
        }
    }
}

.stat-number {
    font-size: 56px;
    font-weight: 700;
    color: #1A1A2E;
    line-height: 1;
    margin-bottom: 8px;

    @media (max-width: 599px) {
        font-size: 42px;
    }
}

.stat-label {
    font-size: 16px;
    color: #5D6E7F;
    font-weight: 500;
}

// Features Section
.features-section {
    .section-title {
        font-size: 28px;
    }
}

.feature-col {
    padding: 10px;
}

.feature-card {
    display: flex;
    align-items: flex-start;
    background: #FFFFFF;
    border-radius: 20px;
    padding: 24px;
    height: 100%;
    min-height: 140px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.3s ease;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }
}

.feature-icon {
    flex-shrink: 0;
    margin-right: 14px;
    margin-top: 0;
}

.feature-content {
    flex: 1;
}

.feature-title {
    font-size: 16px;
    font-weight: 700;
    color: #1A1A2E;
    margin-bottom: 8px;
    line-height: 1.3;
}

.feature-description {
    font-size: 14px;
    color: #6B7280;
    margin: 0;
    line-height: 1.6;
}

// How It Works Section
.how-it-works-section {
    background: #FFFFFF;
    padding: 80px 0;
}

.how-it-works-image-col {
    @media (max-width: 959px) {
        margin-bottom: 40px;
    }
}

.how-it-works-image {
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.how-it-works-steps-col {
    padding-left: 60px;

    @media (max-width: 959px) {
        padding-left: 12px;
    }
}

.step-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 32px;

    &:last-child {
        margin-bottom: 0;
    }
}

.step-number-wrapper {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    background: #2D76E3;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 20px;
}

.step-number {
    font-size: 20px;
    font-weight: 700;
    color: #FFFFFF;
}

.step-content {
    flex: 1;
    padding-top: 2px;
}

.step-title {
    font-size: 17px;
    font-weight: 700;
    color: #1A1A2E;
    margin-bottom: 6px;
    line-height: 1.3;
}

.step-description {
    font-size: 14px;
    color: #6B7280;
    margin: 0;
    line-height: 1.6;
}

.how-it-works-btn {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 13px !important;
    letter-spacing: 0.5px;
    padding: 0 24px !important;
    height: 44px !important;
}

// Testimonials Section
.testimonials-section {
    padding: 60px 0;
    background-color: #F5F5F5;
}

.testimonial-col {
    padding: 12px;
}

.testimonial-card {
    background: #FFFFFF;
    border-radius: 16px;
    padding: 32px 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.quote-icon-wrapper {
    margin-bottom: 16px;
}

.quote-icon-circle {
    width: 56px;
    height: 56px;
    background-color: #afdf62;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.stars-wrapper {
    margin-bottom: 16px;
}

.testimonial-quote {
    font-size: 14px;
    color: #5D6E7F;
    line-height: 1.7;
    flex: 1;
    margin-bottom: 20px;
}

.testimonial-name {
    font-size: 16px;
    font-weight: 600;
    color: #1A1A2E;
    margin: 0;
}

// CTA Footer Section
.cta-footer-section {
    padding: 80px 0;
}

.cta-title {
    font-size: 36px;
    font-weight: 700;
    line-height: 1.3;

    @media (max-width: 599px) {
        font-size: 28px;
    }
}

.cta-description {
    font-size: 16px;
    opacity: 0.9;
}

.cta-subtitle {
    font-size: 18px;
    font-weight: 500;
    opacity: 0.8;
}

.cta-btn {
    min-width: 200px;
    height: 56px !important;
    font-size: 18px !important;
    text-transform: none;
    border-radius: 8px;
}

// Simple Steps Section
.simple-steps-section {
    background: #FFFFFF;
    padding: 80px 0;

    .section-title {
        font-size: 32px;
        margin-bottom: 20px;

        @media (max-width: 599px) {
            font-size: 26px;
        }
    }

    ::v-deep .steps-content {
        margin-top: 40px;

        .step {
            h3 {
                color: #2D76E3;
                font-size: 18px;
                font-weight: 700;
            }

            p {
                font-size: 14px;
                color: #5D6E7F;
                line-height: 1.6;

                strong {
                    color: #1A1A2E;
                }
            }
        }
    }
}

.cta-steps-btn {
    min-width: 180px;
    height: 48px !important;
    font-size: 15px !important;
    font-weight: 600;
    text-transform: none;
    border-radius: 6px;
    letter-spacing: 0;
}

// Start Here Section
.start-here-section {
    background: linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%);
    padding: 80px 0;
}

.housing-card-col {
    padding: 12px;
}

.housing-card {
    background: #FFFFFF;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    height: 100%;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
}

.housing-card-header {
    padding: 32px 24px;
    text-align: center;

    &.shared {
        background: linear-gradient(135deg, #afdf62 0%, #8BC34A 100%);
    }

    &.direct {
        background: linear-gradient(135deg, #2D76E3 0%, #1E5BBF 100%);
    }

    h3 {
        color: #FFFFFF;
        font-size: 24px;
        font-weight: 700;
        margin-top: 12px;
        margin-bottom: 0;
    }
}

.housing-card-body {
    padding: 24px;
}

.housing-features {
    list-style: none;
    padding: 0;
    margin: 0 0 20px 0;

    li {
        display: flex;
        align-items: center;
        padding: 10px 0;
        border-bottom: 1px solid #F0F0F0;

        &:last-child {
            border-bottom: none;
        }

        .v-icon {
            margin-right: 12px;
            flex-shrink: 0;
        }

        span {
            font-size: 15px;
            color: #4A5568;
        }
    }
}

.housing-ideal {
    font-size: 14px;
    color: #718096;
    font-style: italic;
    text-align: center;
    margin: 0;
    padding-top: 8px;
}

.start-here-btn {
    min-width: 220px;
    height: 56px !important;
    font-size: 18px !important;
    font-weight: 600;
    text-transform: none;
    border-radius: 8px;
    letter-spacing: 0;
}

.start-here-subtext {
    font-size: 14px;
    color: #718096;
    margin-top: 16px;
    margin-bottom: 0;
}
</style>
